/**
 * Wallet MCP Tools
 *
 * Exposes wallet authentication and management to AI agents via MCP.
 * Enables third-party agents to authenticate with 0works using Web3 wallets
 * without needing to implement the signing flow themselves.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import { walletLogin, getCurrentUser } from '../api/auth.js';
import { isAuthenticated } from '../utils/config.js';
import { getApiClient } from '../api/client.js';

export function getWalletToolDefinitions(): Tool[] {
  return [
    {
      name: 'paean_wallet_login',
      description:
        'Authenticate with Paean AI / 0works using a wallet private key. ' +
        'Performs the full nonce-request, message-signing, and login flow internally. ' +
        'After success, the JWT token is stored and all other paean tools become authenticated. ' +
        'For EVM: provide the private key as hex (with or without 0x prefix). ' +
        'For Solana: provide the secret key as base64-encoded 64-byte keypair.',
      inputSchema: {
        type: 'object',
        properties: {
          chain: {
            type: 'string',
            enum: ['evm', 'solana'],
            description: 'Blockchain to authenticate with. Must be exactly "evm" or "solana".',
          },
          privateKey: {
            type: 'string',
            description: 'Wallet private key (hex for EVM, base64 for Solana). Not stored to disk — only used for signing.',
          },
        },
        required: ['chain', 'privateKey'],
      },
    },
    {
      name: 'paean_wallet_status',
      description:
        'Check the current wallet authentication status and account information. ' +
        'Returns whether the agent is authenticated, the user profile, and credit balance.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'paean_wallet_generate',
      description:
        'Generate a new EVM or Solana wallet keypair for agent use. ' +
        'Returns the address and private key. The private key is NOT stored — ' +
        'you must save it yourself. Use the returned private key with paean_wallet_login to authenticate.',
      inputSchema: {
        type: 'object',
        properties: {
          chain: {
            type: 'string',
            enum: ['evm', 'solana'],
            description: 'Which chain to generate a keypair for',
          },
        },
        required: ['chain'],
      },
    },
  ];
}

export const WALLET_TOOL_NAMES = new Set([
  'paean_wallet_login',
  'paean_wallet_status',
  'paean_wallet_generate',
]);

export async function executeWalletTool(
  toolName: string,
  args: Record<string, unknown>,
): Promise<unknown> {
  switch (toolName) {
    case 'paean_wallet_login':
      return walletLoginTool(args);
    case 'paean_wallet_status':
      return walletStatusTool();
    case 'paean_wallet_generate':
      return walletGenerateTool(args);
    default:
      return { success: false, error: `Unknown wallet tool: ${toolName}` };
  }
}

async function walletLoginTool(args: Record<string, unknown>): Promise<unknown> {
  const chain = args.chain as string;
  const privateKey = args.privateKey as string;

  if (!chain || (chain !== 'evm' && chain !== 'solana')) {
    return {
      success: false,
      error: 'chain must be exactly "evm" or "solana"',
    };
  }

  if (!privateKey) {
    return {
      success: false,
      error: 'privateKey is required',
    };
  }

  try {
    const result = await walletLogin(chain, privateKey);

    if (result.success) {
      return {
        success: true,
        message: 'Wallet authentication successful. All paean tools are now authenticated.',
        chain,
        walletAddress: result.address,
        hint: 'You can now use paean_0works_browse, paean_0works_claim, and other 0works tools.',
      };
    }

    return {
      success: false,
      error: result.error || 'Wallet login failed',
      chain,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Wallet login failed',
    };
  }
}

async function walletStatusTool(): Promise<unknown> {
  const authenticated = isAuthenticated();

  if (!authenticated) {
    return {
      success: true,
      authenticated: false,
      message: 'Not authenticated. Use paean_wallet_login or paean_wallet_generate + paean_wallet_login to authenticate.',
    };
  }

  try {
    const user = await getCurrentUser();
    let credits: unknown = null;

    try {
      const client = getApiClient();
      const creditsRes = await client.get('/works/my/credits');
      credits = creditsRes.data;
    } catch {
      // Credits endpoint may fail if user has no 0works activity
    }

    return {
      success: true,
      authenticated: true,
      user: user ? {
        id: user.id,
        email: user.email,
        name: user.name,
      } : null,
      credits,
    };
  } catch (error) {
    return {
      success: true,
      authenticated: true,
      tokenValid: false,
      error: 'Token may be expired. Re-authenticate with paean_wallet_login.',
    };
  }
}

async function walletGenerateTool(args: Record<string, unknown>): Promise<unknown> {
  const chain = args.chain as string;

  if (!chain || (chain !== 'evm' && chain !== 'solana')) {
    return {
      success: false,
      error: 'chain must be exactly "evm" or "solana"',
    };
  }

  try {
    if (chain === 'evm') {
      const { ethers } = await import('ethers');
      const wallet = ethers.Wallet.createRandom();
      return {
        success: true,
        chain: 'evm',
        address: wallet.address,
        privateKey: wallet.privateKey,
        warning: 'Store this private key securely. It is not saved by the CLI. You will need it to authenticate with paean_wallet_login.',
        next_step: `Call paean_wallet_login with chain="evm" and privateKey="${wallet.privateKey}" to authenticate.`,
      };
    } else {
      const { Keypair } = await import('@solana/web3.js');
      const keypair = Keypair.generate();
      const secretKeyBase64 = Buffer.from(keypair.secretKey).toString('base64');
      return {
        success: true,
        chain: 'solana',
        address: keypair.publicKey.toBase58(),
        privateKey: secretKeyBase64,
        privateKeyFormat: 'base64-encoded 64-byte Ed25519 secret key',
        warning: 'Store this private key securely. It is not saved by the CLI. You will need it to authenticate with paean_wallet_login.',
        next_step: `Call paean_wallet_login with chain="solana" and the privateKey to authenticate.`,
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to generate wallet',
    };
  }
}
